// Styles of the Track & Trace page, scoped under .tt so the page can also be rendered as a preview inside the
// planner. Colours and text styles of the Veloyd Design System, the reseller colour comes in through --tt-accent,
// --tt-accent-soft and --tt-on-accent. The desktop layout follows the width of the page itself (container query),
// so the preview in the settings can show it too.
export const trackTraceCss = `
.tt {
    --tt-ground: #FAFAFA;
    --tt-surface: #FFFFFF;
    --tt-text: #646464;
    --tt-ink: #242424;
    --tt-line: #ECECEC;
    --tt-border: #BEBEBE;
    --tt-inactive: #A8A8A8;
    --tt-warn-bg: #FFF4E5;
    --tt-warn-ink: #7A4100;
    --tt-warn: #E08600;
    --tt-error: #D32F2F;
    --tt-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);

    container: tt / inline-size;
    box-sizing: border-box;
    min-height: 100%;
    background: var(--tt-ground);
    color: var(--tt-text);
    font-family: 'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.1px;
    -webkit-font-smoothing: antialiased;
    padding-bottom: 24px;
}
/* Resets without specificity, so the component classes below always win */
:where(.tt) *, :where(.tt) *::before, :where(.tt) *::after { box-sizing: border-box; }
:where(.tt) :where(h1, h2, h3, p, dl, dd) { margin: 0; }
:where(.tt) :where(button, input, textarea) { font: inherit; color: inherit; letter-spacing: inherit; }
:where(.tt) a { color: inherit; }
.tt :focus-visible { outline: 2px solid var(--tt-accent-ink); outline-offset: 2px; }
.tt .mdi { line-height: 1; display: inline-flex; }

/* Text styles of the design system */
.tt-title-medium { font-size: 16px; line-height: 24px; font-weight: 500; letter-spacing: 0.15px; color: var(--tt-ink); }
.tt-title-small { font-size: 14px; line-height: 21px; font-weight: 500; letter-spacing: 0.1px; }
.tt-body-small { font-size: 12px; line-height: 16px; letter-spacing: 0.4px; }
.tt-label-medium { font-size: 12px; line-height: 16px; font-weight: 500; letter-spacing: 0.1px; }

.tt-topbar { position: relative; z-index: 2; background: var(--tt-surface); box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); }
.tt-topbar-inner { max-width: 1164px; margin: 0 auto; padding: 12px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.tt-logo { max-height: 40px; max-width: 180px; object-fit: contain; display: block; }
.tt-logo-name { font-size: 16px; line-height: 24px; font-weight: 500; color: var(--tt-ink); }
.tt-topbar-end { display: flex; align-items: center; gap: 24px; }
.tt-topbar-code { display: none; flex-direction: column; align-items: flex-end; }
.tt-lang { position: relative; }
.tt-lang-button { height: 36px; padding: 0 12px; border-radius: 4px; border: 0; background: var(--tt-surface); box-shadow: var(--tt-shadow); display: flex; align-items: center; gap: 12px; font-size: 14px; font-weight: 500; letter-spacing: 0.25px; cursor: pointer; }
.tt-lang-button .mdi { font-size: 18px; }
.tt-lang-menu { position: absolute; right: 0; top: 42px; z-index: 10; min-width: 180px; padding: 6px; margin: 0; list-style: none; background: var(--tt-surface); border-radius: 4px; box-shadow: var(--tt-shadow); }
.tt-lang-menu button { width: 100%; min-height: 40px; padding: 0 12px; border: 0; border-radius: 4px; background: none; text-align: left; font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; }
.tt-lang-menu button:hover { background: var(--tt-ground); }

.tt-band { height: 24px; background: var(--tt-accent); }
.tt-body { position: relative; }
.tt-body::before { content: ''; position: absolute; left: 0; right: 0; top: 0; height: 36px; background: var(--tt-accent); }

.tt-main { position: relative; max-width: 1164px; margin: 0 auto; padding: 0 12px; display: grid; grid-template-columns: minmax(0, 1fr); gap: 12px; }
.tt-card { background: var(--tt-surface); border-radius: 12px; box-shadow: var(--tt-shadow); padding: 18px; display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.tt-divider { height: 1px; background: var(--tt-line); border: 0; margin: 0; width: 100%; }

.tt-photo { padding: 0; overflow: hidden; }
.tt-photo img { display: block; width: 100%; height: 200px; object-fit: cover; }
.tt-photo-desktop { display: none; }

/* Status: progress, moment and the shipment */
.tt-segments { display: flex; gap: 3px; }
.tt-segment { flex: 1; height: 4px; border-radius: 2px; background: var(--tt-line); }
.tt-segment-done { background: var(--tt-accent); }
.tt-segment-warn { background: var(--tt-warn); }
.tt-progress { display: flex; flex-direction: column; gap: 6px; }
.tt-stepper { display: none; margin: 0; padding: 0; list-style: none; }
.tt-moment { display: flex; flex-direction: column; gap: 3px; }
.tt-headline { font-size: 28px; line-height: 36px; color: var(--tt-ink); font-weight: 400; }
.tt-subline { font-size: 22px; line-height: 28px; color: var(--tt-ink); }
.tt-moment-sub { font-size: 14px; line-height: 21px; }
.tt-notice { display: flex; gap: 12px; padding: 12px; border-radius: 4px; background: var(--tt-warn-bg); color: var(--tt-warn-ink); white-space: pre-line; }
.tt-notice .mdi { font-size: 20px; color: var(--tt-warn); }
.tt-rows { display: flex; flex-direction: column; gap: 12px; }
.tt-row { display: flex; gap: 12px; align-items: center; min-width: 0; }
.tt-row > .mdi { font-size: 24px; flex-shrink: 0; }
.tt-row-text { display: flex; flex-direction: column; min-width: 0; }
.tt-value { font-size: 14px; line-height: 21px; font-weight: 500; overflow-wrap: anywhere; }
.tt-toggle { align-self: flex-start; display: inline-flex; align-items: center; gap: 3px; padding: 0; border: 0; background: none; font-weight: 500; color: var(--tt-ink); text-decoration: underline; cursor: pointer; }
.tt-toggle .mdi { font-size: 20px; }
.tt-details { display: flex; flex-direction: column; gap: 12px; }
.tt-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.tt-collo { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 0; border-top: 1px solid var(--tt-line); }
.tt-badge { flex-shrink: 0; height: 20px; padding: 0 12px; border-radius: 24px; background: var(--tt-line); display: inline-flex; align-items: center; font-size: 12px; letter-spacing: 0.4px; white-space: nowrap; }
.tt-list { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 6px; }
.tt-list li { display: flex; gap: 12px; align-items: flex-start; }
.tt-list .mdi { font-size: 20px; }

/* Pas je bezorging aan */
.tt-actions-intro { display: none; }
.tt-tiles { display: flex; justify-content: space-between; gap: 6px; }
.tt-tile { width: 64px; display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 0; border: 0; background: none; cursor: pointer; text-align: center; color: var(--tt-ink); font-weight: 500; }
.tt-tile-icon { width: 56px; height: 56px; border-radius: 28px; display: flex; align-items: center; justify-content: center; background: var(--tt-accent); color: var(--tt-on-accent); }
.tt-tile-icon .mdi { font-size: 28px; }
.tt-tile-long { display: none; }
.tt-tile:disabled { cursor: default; }
.tt-tile[data-inactive="true"] .tt-tile-icon { background: var(--tt-line); color: var(--tt-text); }
.tt-tile[data-inactive="true"] { color: var(--tt-text); }
.tt-tile:disabled .tt-tile-icon { opacity: 0.6; }
.tt-panel { display: flex; flex-direction: column; gap: 12px; }
.tt-options { display: flex; flex-direction: column; gap: 12px; margin: 0; padding: 0; border: 0; }
.tt-option { display: flex; align-items: center; gap: 12px; min-height: 48px; padding: 6px 12px; border: 1px solid var(--tt-border); border-radius: 4px; background: var(--tt-surface); cursor: pointer; text-align: left; width: 100%; }
.tt-option .mdi { font-size: 24px; color: var(--tt-border); }
.tt-option[aria-checked="true"] { border-color: var(--tt-accent-ink); }
.tt-option[aria-checked="true"] .mdi { color: var(--tt-accent-ink); }
.tt-option-text { display: flex; flex-direction: column; }
.tt-field { display: flex; flex-direction: column; gap: 3px; }
.tt-field label { font-size: 12px; line-height: 16px; letter-spacing: 0.4px; }
.tt-input { width: 100%; min-height: 36px; padding: 6px 0; border: 0; border-bottom: 1px solid var(--tt-border); background: transparent; font-size: 14px; color: var(--tt-ink); }
.tt-input:focus { outline: none; border-bottom-color: var(--tt-accent-ink); }
.tt-textarea { resize: vertical; }
.tt-check { display: flex; gap: 12px; align-items: flex-start; font-size: 12px; line-height: 16px; letter-spacing: 0.4px; }
.tt-check input { width: 18px; height: 18px; margin: 0; flex-shrink: 0; accent-color: var(--tt-text); }
.tt-button { min-height: 36px; padding: 6px 12px; border-radius: 4px; border: 0; background: var(--tt-text); color: #FFFFFF; box-shadow: var(--tt-shadow); font-size: 14px; font-weight: 500; letter-spacing: 0.25px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 12px; }
.tt-button:disabled { background: #D6D6D6; color: #FFFFFF; cursor: default; }
.tt-button-white { background: var(--tt-surface); color: var(--tt-text); }
.tt-button-wide { width: 100%; }
.tt-choice { display: flex; gap: 12px; align-items: flex-start; padding: 12px; border: 1px solid var(--tt-accent-ink); border-radius: 4px; background: var(--tt-surface); }
.tt-choice > .mdi { font-size: 24px; color: var(--tt-accent-ink); }
.tt-choice .tt-value { color: var(--tt-ink); }
.tt-choice-hint { margin-top: 3px; }
.tt-choice-buttons { display: flex; gap: 6px; margin-top: 6px; }
.tt-text-button { min-height: 36px; padding: 6px 12px; border: 0; border-radius: 4px; background: none; font-weight: 500; letter-spacing: 0.25px; cursor: pointer; color: var(--tt-ink); }
.tt-text-button:hover { background: var(--tt-ground); }
.tt-tile[data-chosen="true"] .tt-tile-icon { box-shadow: 0 0 0 3px var(--tt-surface), 0 0 0 5px var(--tt-accent-ink); }
.tt-link-button { padding: 0; background: none; border: 0; font-weight: 500; text-decoration: underline; cursor: pointer; white-space: nowrap; }
.tt-feedback { padding: 12px; border-radius: 4px; background: var(--tt-accent-soft); }
.tt-feedback-error { background: #FDECEA; color: var(--tt-error); }
.tt-error { color: var(--tt-error); font-size: 12px; line-height: 16px; }

/* Bericht en kaart */
.tt-pair { display: grid; grid-template-columns: minmax(0, 1fr); gap: 12px; }
.tt-message { align-items: center; justify-content: center; text-align: center; gap: 24px; padding: 30px 24px; }
.tt-message .tt-logo { max-height: 32px; }
.tt-message-text { font-size: 22px; line-height: 28px; color: var(--tt-ink); }
.tt-map { position: relative; height: 220px; border-radius: 4px; overflow: hidden; background: var(--tt-line); z-index: 0; }
.tt-map-stops { position: absolute; top: 12px; left: 12px; z-index: 500; height: 24px; padding: 0 6px; border-radius: 4px; background: var(--tt-surface); box-shadow: var(--tt-shadow); display: inline-flex; align-items: center; gap: 3px; font-weight: 500; color: var(--tt-ink); }
.tt-marker { width: 36px; height: 36px; border-radius: 18px; border: 2px solid #FFFFFF; display: flex; align-items: center; justify-content: center; box-shadow: var(--tt-shadow); font-size: 20px; }
.tt-marker-home { background: var(--tt-accent); color: var(--tt-on-accent); }
.tt-marker-courier { background: var(--tt-ink); color: #FFFFFF; }

/* Bewijs */
.tt-proof-images { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
.tt-proof-image { padding: 0; border: 0; border-radius: 4px; overflow: hidden; background: var(--tt-line); cursor: zoom-in; aspect-ratio: 4 / 3; }
.tt-proof-image img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tt-signature { max-width: 100%; max-height: 120px; object-fit: contain; align-self: flex-start; background: #FFFFFF; border: 1px solid var(--tt-line); border-radius: 4px; padding: 6px; }

/* Verloop */
.tt-timeline { display: flex; flex-direction: column; margin: 0; padding: 0; list-style: none; }
.tt-event { display: flex; gap: 12px; }
.tt-event-rail { display: flex; flex-direction: column; align-items: center; gap: 3px; padding-top: 3px; flex-shrink: 0; }
.tt-event-dot { width: 12px; height: 12px; border-radius: 6px; background: var(--tt-border); }
.tt-event-dot-current { background: var(--tt-accent); }
.tt-event-dot-warn { background: var(--tt-warn); }
.tt-event-line { flex: 1; width: 2px; background: var(--tt-line); }
.tt-event-body { display: flex; flex-direction: column; padding-bottom: 12px; min-width: 0; }
.tt-event:last-child .tt-event-body { padding-bottom: 0; }

/* Hulp nodig */
.tt-contact { display: flex; gap: 6px; }
.tt-contact a { flex: 1; min-height: 48px; border-radius: 4px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-decoration: none; font-weight: 500; letter-spacing: 0.25px; }
.tt-contact a .mdi { font-size: 18px; }
.tt-contact a:hover { background: var(--tt-ground); }
.tt-faq { display: flex; flex-direction: column; }
.tt-faq-item { border-top: 1px solid var(--tt-border); }
.tt-faq-question { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; background: none; border: 0; text-align: left; font-weight: 500; cursor: pointer; }
.tt-faq-question .mdi { font-size: 24px; transition: transform 0.15s; }
.tt-faq-question[aria-expanded="true"] .mdi { transform: rotate(180deg); }
.tt-faq-answer { padding: 0 0 12px; white-space: pre-line; }

.tt-footer { max-width: 560px; margin: 0 auto; padding: 24px 24px 0; display: flex; flex-direction: column; align-items: center; gap: 3px; text-align: center; }
.tt-footer-message { white-space: pre-line; }

.tt-center { max-width: 480px; margin: 0 auto; padding: 64px 12px; }
.tt-search { display: flex; flex-direction: column; gap: 12px; }
.tt-spinner { width: 36px; height: 36px; border-radius: 50%; border: 4px solid var(--tt-line); border-top-color: var(--tt-accent); animation: tt-spin 0.9s linear infinite; margin: 0 auto 12px; }
@keyframes tt-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .tt-spinner { animation-duration: 3s; } .tt-faq-question .mdi { transition: none; } }

.tt-lightbox { position: fixed; inset: 0; z-index: 1000; background: rgba(36, 36, 36, 0.88); display: flex; align-items: center; justify-content: center; padding: 56px 12px 12px; }
.tt-lightbox img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 4px; background: #FFFFFF; }
.tt-lightbox-close { position: absolute; top: 6px; right: 6px; width: 44px; height: 44px; border-radius: 22px; border: 0; background: rgba(255, 255, 255, 0.16); color: #FFFFFF; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 24px; }

.tt-visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

@container tt (min-width: 1024px) {
    .tt-topbar-inner { padding: 12px 24px; }
    .tt-topbar-code { display: flex; }
    .tt-band { height: 48px; }
    .tt-body::before { height: 110px; }
    .tt-main { grid-template-columns: minmax(0, 1fr) 330px; gap: 24px; padding: 0 24px; }
    .tt-wide { grid-column: 1 / -1; }
    .tt-side { grid-column: 2; grid-row: var(--tt-side-row); align-self: start; }
    .tt-card { padding: 24px; }

    .tt-photo { padding: 0; }
    .tt-photo-mobile { display: none; }
    .tt-photo-desktop { display: block; }
    .tt-photo img { height: 190px; }

    .tt-status-head { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; }
    .tt-progress { display: none; }
    .tt-stepper { display: flex; flex: 0 0 auto; width: 50%; max-width: 520px; }
    .tt-step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; position: relative; font-size: 12px; line-height: 16px; letter-spacing: 0.4px; }
    .tt-step-dot { position: relative; z-index: 1; width: 24px; height: 24px; border-radius: 12px; background: var(--tt-line); border: 1px solid var(--tt-border); display: flex; align-items: center; justify-content: center; color: var(--tt-on-accent); font-size: 16px; }
    .tt-step-done .tt-step-dot { background: var(--tt-accent); border-color: var(--tt-accent); }
    .tt-step-current .tt-step-dot { background: var(--tt-surface); border: 2px solid var(--tt-accent); }
    .tt-step-current .tt-step-dot::after { content: ''; width: 10px; height: 10px; border-radius: 5px; background: var(--tt-accent); }
    .tt-step-warn .tt-step-dot { border-color: var(--tt-warn); }
    .tt-step-warn .tt-step-dot::after { background: var(--tt-warn); }
    .tt-step-current { font-weight: 500; color: var(--tt-ink); font-size: 14px; line-height: 21px; }
    .tt-step:not(:last-child)::after { content: ''; position: absolute; top: 12px; left: calc(50% + 24px); right: calc(-50% + 24px); height: 2px; background: var(--tt-line); }
    .tt-step-done:not(:last-child)::after { background: var(--tt-accent); }
    .tt-headline { font-size: 32px; line-height: 40px; }
    .tt-rows { flex-direction: row; }
    .tt-rows > * { flex: 1; }

    .tt-actions-intro { display: block; }
    .tt-actions .tt-title-medium { font-size: 22px; line-height: 28px; font-weight: 400; }
    .tt-tiles { justify-content: flex-start; gap: 12px; }
    .tt-tile { width: auto; flex: 1; max-width: 136px; min-height: 150px; padding: 12px; border: 1px solid var(--tt-border); border-radius: 4px; justify-content: flex-start; font-size: 16px; line-height: 24px; }
    .tt-tile-icon { width: 46px; height: 46px; border-radius: 23px; }
    .tt-tile-icon .mdi { font-size: 24px; }
    .tt-tile-short { display: none; }
    .tt-tile-long { display: inline; }
    .tt-tile[aria-expanded="true"], .tt-tile[data-chosen="true"] { border-color: var(--tt-accent-ink); }
    .tt-tile[data-chosen="true"] .tt-tile-icon { box-shadow: none; }
    .tt-panel { max-width: 560px; }

    .tt-pair { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .tt-pair > :only-child { grid-column: 1 / -1; }
    .tt-map { height: 230px; }
}
`
